import { Link, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { canPerform } from "@vital/shared";
import { useAuth, useActiveOrganization } from "../context/AuthContext";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/Card";
import { PasswordConfirmModal } from "../components/PasswordConfirmModal";
import { OpsEmptyState, SectionHeader, InventorySkeleton } from "../components/ops-display";
import { InspectPanel } from "../components/InspectPanel";
import {
  FleetDeployFilterBar,
  FleetDeployInspectSummary,
  FleetDeployInventoryList,
  FleetDriftFilterBar,
  FleetDriftInspectSummary,
  FleetDriftInventoryList,
  FleetSiteFilterBar,
  FleetSiteInspectSummary,
  FleetSiteInventoryList,
} from "../components/fleet-inventory";
import {
  api,
  ApiError,
  type FleetDeploymentRow,
  type FleetDriftRow,
  type PortalBundleJob,
  type RouterInventoryRow,
} from "../lib/api";
import { RING_LABELS } from "../lib/fleet-labels";
import {
  FLEET_AUTHORITY_NOTE,
  FLEET_DATA_BOUNDARY_NOTE,
  buildFleetSiteRows,
  filterAndSortDeployments,
  filterAndSortDrift,
  filterAndSortSites,
  toFleetDeployRow,
  toFleetDriftListRow,
  truncateSha256,
  type DeploySortKey,
  type DriftSortKey,
  type FleetDeployRow,
  type FleetDriftListRow,
  type SiteSortKey,
} from "../lib/fleet-ops";

type Tab = "inventory" | "deployments" | "drift";

type AdminSite = {
  id: string;
  code: string;
  name: string;
  location: string | null;
  active?: boolean;
};

type PasswordAction =
  | { kind: "promote_production"; siteId: string; payload: InventoryForm }
  | { kind: "verify_mismatch"; deploymentId: string }
  | { kind: "waive_drill"; deploymentId: string };

type InventoryForm = {
  serialAssetTag: string;
  managementHost: string;
  routerModel: string;
  routerosVersion: string;
  hotspotProfileName: string;
  portalDirectoryPath: string;
  ring: "pilot" | "canary" | "production";
  notes: string;
};

const EMPTY_FORM: InventoryForm = {
  serialAssetTag: "",
  managementHost: "",
  routerModel: "",
  routerosVersion: "",
  hotspotProfileName: "",
  portalDirectoryPath: "",
  ring: "pilot",
  notes: "",
};

function formFromInventory(row: RouterInventoryRow | null): InventoryForm {
  if (!row) return { ...EMPTY_FORM };
  return {
    serialAssetTag: row.serialAssetTag ?? "",
    managementHost: row.managementHost ?? "",
    routerModel: row.routerModel ?? "",
    routerosVersion: row.routerosVersion ?? "",
    hotspotProfileName: row.hotspotProfileName ?? "",
    portalDirectoryPath: row.portalDirectoryPath ?? "",
    ring: row.ring,
    notes: row.notes ?? "",
  };
}

export function FleetPage() {
  const { user } = useAuth();
  const activeOrg = useActiveOrganization();
  const authInput = {
    platformRole: user?.platformRole ?? null,
    membershipRole: user?.membershipRole ?? null,
    legacyRole: user?.role,
  };
  const canRead = canPerform({ ...authInput, permission: "fleet.inventory.read" });
  const canManageInventory = canPerform({ ...authInput, permission: "fleet.inventory.manage" });
  const canRecord = canPerform({ ...authInput, permission: "fleet.deployment.record" });
  const canManageDeploy = canPerform({ ...authInput, permission: "fleet.deployment.manage" });
  const canPlatformDrift =
    user?.platformRole === "owner" || user?.platformRole === "operator";
  const writable = activeOrg.writable;
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get("tab");
  const tab: Tab =
    tabParam === "deployments" || tabParam === "drift" ? tabParam : "inventory";

  function setTab(next: Tab) {
    const nextParams = new URLSearchParams(searchParams);
    if (next === "inventory") nextParams.delete("tab");
    else nextParams.set("tab", next);
    setSearchParams(nextParams, { replace: true });
  }

  const [sites, setSites] = useState<AdminSite[]>([]);
  const [inventory, setInventory] = useState<RouterInventoryRow[]>([]);
  const [deployments, setDeployments] = useState<FleetDeploymentRow[]>([]);
  const [drift, setDrift] = useState<FleetDriftRow[]>([]);
  const [platformDrift, setPlatformDrift] = useState(false);
  const [bundleHistory, setBundleHistory] = useState<PortalBundleJob[]>([]);

  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [form, setForm] = useState<InventoryForm>(EMPTY_FORM);
  const [currentRing, setCurrentRing] = useState<"pilot" | "canary" | "production">("pilot");

  const [siteQuery, setSiteQuery] = useState("");
  const [siteFilter, setSiteFilter] = useState("all");
  const [siteSort, setSiteSort] = useState<SiteSortKey>("name");

  const [deployQuery, setDeployQuery] = useState("");
  const [deployFilter, setDeployFilter] = useState("all");
  const [deploySort, setDeploySort] = useState<DeploySortKey>("intended");
  const [selectedDeployId, setSelectedDeployId] = useState<string | null>(null);

  const [driftQuery, setDriftQuery] = useState("");
  const [driftFilter, setDriftFilter] = useState("all");
  const [driftSort, setDriftSort] = useState<DriftSortKey>("site");
  const [selectedDriftKey, setSelectedDriftKey] = useState<string | null>(null);

  const [intentSiteId, setIntentSiteId] = useState("");
  const [intentArtifactId, setIntentArtifactId] = useState("");
  const [intentNotes, setIntentNotes] = useState("");

  const [installReleaseId, setInstallReleaseId] = useState("");
  const [installSha, setInstallSha] = useState("");
  const [installNotes, setInstallNotes] = useState("");
  const [activeDeployId, setActiveDeployId] = useState<string | null>(null);

  const [verifyMatchNotes, setVerifyMatchNotes] = useState("");
  const [mismatchReason, setMismatchReason] = useState("");
  const [mismatchNotes, setMismatchNotes] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [passwordAction, setPasswordAction] = useState<PasswordAction | null>(null);
  const [password, setPassword] = useState("");
  const [confirming, setConfirming] = useState(false);

  const inventoryBySite = useMemo(() => {
    const map = new Map<string, RouterInventoryRow>();
    for (const row of inventory) map.set(row.siteId, row);
    return map;
  }, [inventory]);

  const driftBySite = useMemo(() => {
    const map = new Map<string, FleetDriftRow>();
    for (const row of drift) {
      if (!platformDrift || row.organizationId === activeOrg.organizationId) {
        map.set(row.siteId, row);
      }
    }
    return map;
  }, [drift, platformDrift, activeOrg.organizationId]);

  const selectedSite = useMemo(
    () => sites.find((s) => s.id === selectedSiteId) ?? null,
    [sites, selectedSiteId],
  );

  const siteDeployments = useMemo(() => {
    if (!selectedSiteId) return deployments;
    return deployments.filter((d) => d.siteId === selectedSiteId);
  }, [deployments, selectedSiteId]);

  const readyArtifacts = useMemo(() => {
    return bundleHistory.filter(
      (j) =>
        j.status === "ready" &&
        j.artifact &&
        (!intentSiteId || j.siteId === intentSiteId),
    );
  }, [bundleHistory, intentSiteId]);

  const siteRows = useMemo(
    () =>
      buildFleetSiteRows({
        sites,
        inventoryBySite,
        driftBySite,
      }),
    [sites, inventoryBySite, driftBySite],
  );

  const filteredSites = useMemo(
    () =>
      filterAndSortSites(siteRows, {
        query: siteQuery,
        filter: siteFilter,
        sort: siteSort,
      }),
    [siteRows, siteQuery, siteFilter, siteSort],
  );

  const selectedSiteRow = useMemo(
    () => siteRows.find((r) => r.siteId === selectedSiteId) ?? null,
    [siteRows, selectedSiteId],
  );

  const deployRows = useMemo(() => {
    const byId = new Map(sites.map((s) => [s.id, s]));
    return siteDeployments.map((d) => toFleetDeployRow(d, byId.get(d.siteId)));
  }, [siteDeployments, sites]);

  const filteredDeploys = useMemo(
    () =>
      filterAndSortDeployments(deployRows, {
        query: deployQuery,
        filter: deployFilter,
        sort: deploySort,
      }),
    [deployRows, deployQuery, deployFilter, deploySort],
  );

  const selectedDeployRow = useMemo(() => {
    const id = selectedDeployId ?? activeDeployId;
    if (!id) return null;
    return (
      filteredDeploys.find((d) => d.id === id) ??
      deployRows.find((d) => d.id === id) ??
      null
    );
  }, [selectedDeployId, activeDeployId, filteredDeploys, deployRows]);

  const driftRows = useMemo(() => drift.map(toFleetDriftListRow), [drift]);

  const filteredDrift = useMemo(
    () =>
      filterAndSortDrift(driftRows, {
        query: driftQuery,
        filter: driftFilter,
        sort: driftSort,
      }),
    [driftRows, driftQuery, driftFilter, driftSort],
  );

  const selectedDriftRow = useMemo(
    () => filteredDrift.find((r) => r.key === selectedDriftKey) ?? null,
    [filteredDrift, selectedDriftKey],
  );

  const load = useCallback(async () => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const driftScope = platformDrift && canPlatformDrift ? "platform" : undefined;
      const [siteList, inv, deps, driftRes, history] = await Promise.all([
        api.listSites() as Promise<AdminSite[]>,
        api.listFleetInventory(),
        api.listFleetDeployments(),
        api.listFleetDrift(driftScope),
        canRecord || canManageDeploy
          ? api.listPortalBundleHistory()
          : Promise.resolve({ history: [] as PortalBundleJob[] }),
      ]);
      setSites(siteList);
      setInventory(inv.items);
      setDeployments(deps.items);
      setDrift(driftRes.items);
      setBundleHistory(history.history);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "بارگذاری ناوگان ناموفق بود");
    } finally {
      setLoading(false);
    }
  }, [canRead, canRecord, canManageDeploy, platformDrift, canPlatformDrift]);

  useEffect(() => {
    void load();
  }, [load]);

  function selectSite(siteId: string) {
    setSelectedSiteId(siteId);
    setIntentSiteId(siteId);
    const row = inventoryBySite.get(siteId) ?? null;
    setForm(formFromInventory(row));
    setCurrentRing(row?.ring ?? "pilot");
    setSuccess(null);
    setError(null);
    const open = deployments.find(
      (d) =>
        d.siteId === siteId &&
        (d.phase === "intended" || d.phase === "installed_recorded"),
    );
    setActiveDeployId(open?.id ?? null);
    setSelectedDeployId(open?.id ?? null);
    if (open) {
      setInstallReleaseId(open.expectedTemplateReleaseId);
      setInstallSha(open.artifactSha256 ?? "");
    }
  }

  function selectDeploy(id: string) {
    const d = deployments.find((row) => row.id === id);
    if (!d) return;
    setSelectedDeployId(id);
    setActiveDeployId(id);
    setSelectedSiteId(d.siteId);
    setInstallReleaseId(d.expectedTemplateReleaseId);
    setInstallSha(d.artifactSha256 ?? "");
  }

  async function saveInventory(
    payload: InventoryForm,
    passwordValue?: string,
  ): Promise<boolean> {
    if (!selectedSiteId || !canManageInventory || !writable) return false;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.upsertFleetInventory(selectedSiteId, {
        serialAssetTag: payload.serialAssetTag || null,
        managementHost: payload.managementHost || null,
        routerModel: payload.routerModel || null,
        routerosVersion: payload.routerosVersion || null,
        hotspotProfileName: payload.hotspotProfileName || null,
        portalDirectoryPath: payload.portalDirectoryPath || null,
        ring: payload.ring,
        notes: payload.notes || null,
        ...(passwordValue ? { password: passwordValue } : {}),
      });
      setSuccess(
        payload.ring === "production" && currentRing !== "production"
          ? "حلقه تولید به‌روز شد (فقط CMS — بدون استقرار خودکار)."
          : "موجودی روتر ذخیره شد.",
      );
      setCurrentRing(payload.ring);
      await load();
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ذخیره موجودی ناموفق بود");
      return false;
    } finally {
      setSaving(false);
    }
  }

  function onInventorySubmit(e: FormEvent) {
    e.preventDefault();
    if (!selectedSiteId) return;
    if (form.ring === "production" && currentRing !== "production") {
      setPasswordAction({ kind: "promote_production", siteId: selectedSiteId, payload: form });
      setPassword("");
      return;
    }
    void saveInventory(form);
  }

  async function createIntent(e: FormEvent) {
    e.preventDefault();
    if (!canRecord || !writable) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const created = await api.createFleetDeploymentIntent({
        siteId: intentSiteId,
        bundleArtifactId: intentArtifactId,
        evidenceNotes: intentNotes || null,
      });
      setSuccess("قصد استقرار ثبت شد (نصب دستی؛ بدون اقدام روتر).");
      setIntentNotes("");
      setActiveDeployId(created.id);
      setSelectedDeployId(created.id);
      setInstallReleaseId(created.expectedTemplateReleaseId);
      setInstallSha(created.artifactSha256 ?? "");
      setSelectedSiteId(intentSiteId);
      setTab("deployments");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ثبت قصد ناموفق بود");
    } finally {
      setSaving(false);
    }
  }

  async function recordInstall(e: FormEvent) {
    e.preventDefault();
    if (!activeDeployId || !canRecord || !writable) return;
    setSaving(true);
    setError(null);
    try {
      await api.recordFleetInstall(activeDeployId, {
        installedReleaseId: installReleaseId,
        installedSha256: installSha || null,
        evidenceNotes: installNotes || null,
      });
      setSuccess("ثبت نصب انجام شد (اظهار اپراتور).");
      setInstallNotes("");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ثبت نصب ناموفق بود");
    } finally {
      setSaving(false);
    }
  }

  async function verifyMatch() {
    if (!activeDeployId || !canRecord || !writable) return;
    setSaving(true);
    setError(null);
    try {
      await api.verifyFleetDeployment(activeDeployId, {
        verificationStatus: "verified_match",
        evidenceNotes: verifyMatchNotes || null,
      });
      setSuccess("تأیید تطبیق ثبت شد.");
      setVerifyMatchNotes("");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "تأیید ناموفق بود");
    } finally {
      setSaving(false);
    }
  }

  function startMismatchVerify() {
    if (!activeDeployId) return;
    if (!mismatchReason.trim()) {
      setError("دلیل عدم تطبیق الزامی است.");
      return;
    }
    setPasswordAction({ kind: "verify_mismatch", deploymentId: activeDeployId });
    setPassword("");
  }

  async function cancelDeploy(id: string) {
    if (!canManageDeploy || !writable) return;
    if (!window.confirm("قصد استقرار لغو شود؟ (بدون اقدام روتر)")) return;
    setSaving(true);
    setError(null);
    try {
      await api.cancelFleetDeployment(id);
      setSuccess("قصد استقرار لغو شد.");
      if (activeDeployId === id) setActiveDeployId(null);
      if (selectedDeployId === id) setSelectedDeployId(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "لغو ناموفق بود");
    } finally {
      setSaving(false);
    }
  }

  async function markDrilled(id: string) {
    if (!canManageDeploy || !writable) return;
    setSaving(true);
    setError(null);
    try {
      await api.updateFleetRollbackDrill(id, { rollbackDrillStatus: "drilled" });
      setSuccess("وضعیت تمرین بازگردانی ثبت شد.");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ثبت تمرین ناموفق بود");
    } finally {
      setSaving(false);
    }
  }

  function startWaiveDrill(id: string) {
    setPasswordAction({ kind: "waive_drill", deploymentId: id });
    setPassword("");
  }

  async function onPasswordConfirm(e: FormEvent) {
    e.preventDefault();
    if (!passwordAction) return;
    setConfirming(true);
    setError(null);
    try {
      if (passwordAction.kind === "promote_production") {
        const ok = await saveInventory(passwordAction.payload, password);
        if (!ok) return;
      } else if (passwordAction.kind === "verify_mismatch") {
        await api.verifyFleetDeployment(passwordAction.deploymentId, {
          verificationStatus: "verified_mismatch",
          driftReason: mismatchReason,
          evidenceNotes: mismatchNotes || null,
          password,
        });
        setSuccess("عدم تطبیق با تأیید رمز ثبت شد (بدون اقدام روتر).");
        setMismatchReason("");
        setMismatchNotes("");
        await load();
      } else if (passwordAction.kind === "waive_drill") {
        await api.updateFleetRollbackDrill(passwordAction.deploymentId, {
          rollbackDrillStatus: "waived",
          password,
        });
        setSuccess("معافیت تمرین بازگردانی ثبت شد.");
        await load();
      }
      setPasswordAction(null);
      setPassword("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "تأیید رمز ناموفق بود");
    } finally {
      setConfirming(false);
    }
  }

  const activeDeploy = deployments.find((d) => d.id === activeDeployId) ?? null;

  function renderDeployActions(row: FleetDeployRow): ReactNode {
    return (
      <>
        {(row.phase === "intended" || row.phase === "installed_recorded") && (
          <button
            type="button"
            className="btn btn--sm btn--ghost"
            onClick={() => selectDeploy(row.id)}
          >
            انتخاب برای ثبت/تأیید
          </button>
        )}
        {canManageDeploy &&
          writable &&
          (row.phase === "intended" || row.phase === "installed_recorded") && (
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              onClick={() => void cancelDeploy(row.id)}
            >
              لغو
            </button>
          )}
        {canManageDeploy && writable && row.rollbackDrillStatus === "not_started" && (
          <>
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              onClick={() => void markDrilled(row.id)}
            >
              تمرین انجام شد
            </button>
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              onClick={() => startWaiveDrill(row.id)}
            >
              معافیت تمرین
            </button>
          </>
        )}
      </>
    );
  }

  function renderDriftActions(row: FleetDriftListRow): ReactNode {
    if (platformDrift) return null;
    return (
      <button
        type="button"
        className="btn btn--sm btn--ghost"
        onClick={() => {
          selectSite(row.siteId);
          setTab("inventory");
        }}
      >
        مشاهده موجودی
      </button>
    );
  }

  if (!canRead) {
    return (
      <div className="page">
        <PageHeader title="ناوگان و استقرار" />
        <p className="banner banner--error" role="alert">
          مجوز fleet.inventory.read لازم است.
        </p>
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader title="ناوگان و استقرار دستی" />

      <aside
        className="ops-safety-summary ops-safety-summary--warn"
        role="note"
        data-vt-fleet-safety
      >
        <p className="ops-safety-summary__eyebrow">اختیار ناوگان</p>
        <ul className="ops-safety-summary__points">
          <li>قصد استقرار ≠ نصب کامل؛ بستهٔ آماده ≠ تأیید تولید</li>
          <li>
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              rc.21
            </bdi>{" "}
            تولید staged:{" "}
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              deferred/unexecuted
            </bdi>
          </li>
          <li>
            اتوماسیون RouterOS: <strong className="ops-safety-summary__emphasis">NO-GO</strong>
          </li>
        </ul>
        <p className="ops-safety-summary__secondary">
          موجودی فقط فراداده غیرمحرمانه — بدون رمز، کلید خصوصی، توکن API یا توپولوژی.
        </p>
        <details className="ops-safety-disclosure">
          <summary>متن کامل اختیار و مرز داده</summary>
          <p>{FLEET_AUTHORITY_NOTE}</p>
          <p>{FLEET_DATA_BOUNDARY_NOTE}</p>
        </details>
      </aside>

      {!writable && (
        <p className="form-error" role="status">
          سازمان فعلی فقط‌خواندنی است؛ ثبت یا ویرایش ناوگان ممکن نیست.
        </p>
      )}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {success && (
        <p className="form-success" role="status">
          {success}
        </p>
      )}

      <div className="tab-row" role="tablist">
        <button
          type="button"
          role="tab"
          className={`tab ${tab === "inventory" ? "tab--active" : ""}`}
          aria-selected={tab === "inventory"}
          onClick={() => setTab("inventory")}
        >
          موجودی روتر
        </button>
        <button
          type="button"
          role="tab"
          className={`tab ${tab === "deployments" ? "tab--active" : ""}`}
          aria-selected={tab === "deployments"}
          onClick={() => setTab("deployments")}
        >
          استقرار دستی
        </button>
        <button
          type="button"
          role="tab"
          className={`tab ${tab === "drift" ? "tab--active" : ""}`}
          aria-selected={tab === "drift"}
          onClick={() => setTab("drift")}
        >
          ناهماهنگی تنظیمات
        </button>
      </div>

      {loading ? (
        <InventorySkeleton />
      ) : tab === "inventory" ? (
        <div className="page-stack page-stack--inventory">
          <div
            className={`content-ops-layout${selectedSiteRow ? " content-ops-layout--inspect-open" : ""}`}
            data-vt-content-ops
            data-vt-inspect-open={selectedSiteRow ? "true" : "false"}
          >
            <Card className="content-ops-layout__list inventory-surface" bodyClassName="card__body--flush">
              <SectionHeader title="موجودی روتر" />
              <FleetSiteFilterBar
                filter={siteFilter}
                onFilterChange={setSiteFilter}
                query={siteQuery}
                onQueryChange={setSiteQuery}
                sort={siteSort}
                onSortChange={setSiteSort}
              />
              {sites.length === 0 ? (
                <OpsEmptyState title="سایتی نیست" />
              ) : filteredSites.length === 0 ? (
                <OpsEmptyState title="موردی در این فیلتر نیست" />
              ) : (
                <FleetSiteInventoryList
                  rows={filteredSites}
                  selectedId={selectedSiteId}
                  onSelect={selectSite}
                />
              )}
            </Card>

            {selectedSiteRow && (
              <InspectPanel title="خلاصه موجودی" className="content-inspect-panel">
                <FleetSiteInspectSummary item={selectedSiteRow} />
              </InspectPanel>
            )}
          </div>

          <Card title={selectedSite ? `جزئیات: ${selectedSite.name}` : "جزئیات سایت"}>
            {!selectedSite ? (
              <OpsEmptyState title="یک سایت را انتخاب کنید" />
            ) : (
              <form className="content-form" onSubmit={onInventorySubmit}>
                <label>
                  <span>برچسب دارایی / سریال</span>
                  <input
                    dir="ltr"
                    className="ltr-isolate"
                    value={form.serialAssetTag}
                    disabled={!canManageInventory || !writable}
                    onChange={(e) => setForm((f) => ({ ...f, serialAssetTag: e.target.value }))}
                  />
                </label>
                <label>
                  <span>میزبان / آدرس مدیریت</span>
                  <input
                    dir="ltr"
                    className="ltr-isolate"
                    value={form.managementHost}
                    disabled={!canManageInventory || !writable}
                    onChange={(e) => setForm((f) => ({ ...f, managementHost: e.target.value }))}
                  />
                </label>
                <label>
                  <span>مدل روتر</span>
                  <input
                    value={form.routerModel}
                    disabled={!canManageInventory || !writable}
                    onChange={(e) => setForm((f) => ({ ...f, routerModel: e.target.value }))}
                  />
                </label>
                <label>
                  <span>نسخه RouterOS</span>
                  <input
                    dir="ltr"
                    className="ltr-isolate"
                    value={form.routerosVersion}
                    disabled={!canManageInventory || !writable}
                    onChange={(e) => setForm((f) => ({ ...f, routerosVersion: e.target.value }))}
                  />
                </label>
                <label>
                  <span>نام پروفایل هات‌اسپات</span>
                  <input
                    dir="ltr"
                    className="ltr-isolate"
                    value={form.hotspotProfileName}
                    disabled={!canManageInventory || !writable}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, hotspotProfileName: e.target.value }))
                    }
                  />
                </label>
                <label>
                  <span>مسیر پوشه پورتال</span>
                  <input
                    dir="ltr"
                    className="ltr-isolate"
                    value={form.portalDirectoryPath}
                    disabled={!canManageInventory || !writable}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, portalDirectoryPath: e.target.value }))
                    }
                  />
                </label>
                <label>
                  <span>حلقه انتشار</span>
                  <select
                    value={form.ring}
                    disabled={!canManageInventory || !writable}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        ring: e.target.value as InventoryForm["ring"],
                      }))
                    }
                  >
                    <option value="pilot">{RING_LABELS.pilot}</option>
                    <option value="canary">{RING_LABELS.canary}</option>
                    <option value="production">{RING_LABELS.production}</option>
                  </select>
                </label>
                <label>
                  <span>یادداشت عملیاتی (بدون رمز یا کلید)</span>
                  <textarea
                    rows={3}
                    value={form.notes}
                    disabled={!canManageInventory || !writable}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  />
                </label>
                {canManageInventory && writable && (
                  <button type="submit" className="btn btn--primary" disabled={saving} aria-busy={saving}>
                    {saving ? "در حال ذخیره…" : "ذخیره موجودی"}
                  </button>
                )}
                {driftBySite.get(selectedSite.id) && (
                  <p className="field-hint field-hint--block">
                    {driftBySite.get(selectedSite.id)!.driftLabel}
                  </p>
                )}
              </form>
            )}
          </Card>
        </div>
      ) : tab === "deployments" ? (
        <div className="page-stack page-stack--inventory">
          {canRecord && writable && (
            <Card title="ثبت قصد استقرار">
              <form className="content-form" onSubmit={createIntent}>
                <label>
                  <span>سایت</span>
                  <select
                    value={intentSiteId}
                    required
                    onChange={(e) => {
                      setIntentSiteId(e.target.value);
                      setIntentArtifactId("");
                    }}
                  >
                    <option value="">انتخاب…</option>
                    {sites.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({s.code})
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>بسته آماده</span>
                  <select
                    value={intentArtifactId}
                    required
                    disabled={!intentSiteId}
                    onChange={(e) => setIntentArtifactId(e.target.value)}
                  >
                    <option value="">انتخاب…</option>
                    {readyArtifacts.map((j) => (
                      <option key={j.artifact!.id} value={j.artifact!.id}>
                        {j.templateReleaseId} · {j.artifact!.generatedFilename} ·{" "}
                        {truncateSha256(j.artifact!.artifactSha256)}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>یادداشت شواهد</span>
                  <textarea
                    rows={2}
                    value={intentNotes}
                    onChange={(e) => setIntentNotes(e.target.value)}
                    placeholder="مثلاً: آماده کپی دستی از طریق Winbox"
                  />
                </label>
                <button type="submit" className="btn btn--primary" disabled={saving || !intentSiteId} aria-busy={saving}>
                  {saving ? "…" : "ثبت قصد"}
                </button>
                <p className="field-hint field-hint--block">
                  بسته را از صفحه <Link to="/portal-bundles">Bundle</Link> بسازید و دانلود کنید؛ این
                  صفحه فقط قصد و شواهد را ثبت می‌کند. بستهٔ آماده تأیید تولید نیست.
                </p>
              </form>
            </Card>
          )}

          <div
            className={`content-ops-layout${selectedDeployRow ? " content-ops-layout--inspect-open" : ""}`}
            data-vt-content-ops
            data-vt-inspect-open={selectedDeployRow ? "true" : "false"}
          >
            <Card className="content-ops-layout__list inventory-surface" bodyClassName="card__body--flush">
              <SectionHeader title="تاریخچه استقرار" />
              <FleetDeployFilterBar
                filter={deployFilter}
                onFilterChange={setDeployFilter}
                query={deployQuery}
                onQueryChange={setDeployQuery}
                sort={deploySort}
                onSortChange={setDeploySort}
              />
              {siteDeployments.length === 0 ? (
                <OpsEmptyState title="استقراری ثبت نشده" />
              ) : filteredDeploys.length === 0 ? (
                <OpsEmptyState title="موردی در این فیلتر نیست" />
              ) : (
                <FleetDeployInventoryList
                  rows={filteredDeploys}
                  selectedId={selectedDeployId ?? activeDeployId}
                  onSelect={selectDeploy}
                  renderActions={renderDeployActions}
                />
              )}
            </Card>

            {selectedDeployRow && (
              <InspectPanel title="جزئیات قصد استقرار" className="content-inspect-panel">
                <FleetDeployInspectSummary
                  item={selectedDeployRow}
                  actions={renderDeployActions(selectedDeployRow)}
                />
              </InspectPanel>
            )}
          </div>

          {canRecord && writable && activeDeploy && (
            <Card title="ثبت نصب و تأیید">
              {(activeDeploy.phase === "intended" ||
                activeDeploy.phase === "installed_recorded") && (
                <form className="content-form" onSubmit={recordInstall}>
                  <label>
                    <span>نسخه نصب‌شده (اظهار اپراتور)</span>
                    <input
                      dir="ltr"
                      className="ltr-isolate"
                      required
                      value={installReleaseId}
                      onChange={(e) => setInstallReleaseId(e.target.value)}
                    />
                  </label>
                  <label>
                    <span>SHA-256 نصب‌شده (اختیاری)</span>
                    <input
                      dir="ltr"
                      className="ltr-isolate"
                      value={installSha}
                      onChange={(e) => setInstallSha(e.target.value)}
                    />
                  </label>
                  <label>
                    <span>یادداشت شواهد</span>
                    <textarea
                      rows={2}
                      value={installNotes}
                      onChange={(e) => setInstallNotes(e.target.value)}
                    />
                  </label>
                  <button type="submit" className="btn btn--primary" disabled={saving}>
                    ثبت نصب
                  </button>
                </form>
              )}

              <div className="content-form" style={{ marginBlockStart: "1rem" }}>
                <label>
                  <span>یادداشت تأیید تطبیق</span>
                  <textarea
                    rows={2}
                    value={verifyMatchNotes}
                    onChange={(e) => setVerifyMatchNotes(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={saving || activeDeploy.phase === "cancelled"}
                  onClick={() => void verifyMatch()}
                >
                  تأیید تطبیق
                </button>

                <label>
                  <span>دلیل عدم تطبیق (الزامی)</span>
                  <textarea
                    rows={2}
                    value={mismatchReason}
                    onChange={(e) => setMismatchReason(e.target.value)}
                  />
                </label>
                <label>
                  <span>یادداشت عدم تطبیق</span>
                  <textarea
                    rows={2}
                    value={mismatchNotes}
                    onChange={(e) => setMismatchNotes(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="btn btn--ghost"
                  disabled={saving}
                  onClick={startMismatchVerify}
                >
                  ثبت عدم تطبیق (نیاز به رمز)
                </button>
              </div>
            </Card>
          )}
        </div>
      ) : (
        <div className="page-stack page-stack--inventory">
          <div
            className={`content-ops-layout${selectedDriftRow ? " content-ops-layout--inspect-open" : ""}`}
            data-vt-content-ops
            data-vt-inspect-open={selectedDriftRow ? "true" : "false"}
          >
            <Card className="content-ops-layout__list inventory-surface" bodyClassName="card__body--flush">
              <SectionHeader title="گزارش انحراف" />
              <FleetDriftFilterBar
                filter={driftFilter}
                onFilterChange={setDriftFilter}
                query={driftQuery}
                onQueryChange={setDriftQuery}
                sort={driftSort}
                onSortChange={setDriftSort}
                platformDrift={platformDrift}
                canPlatformDrift={canPlatformDrift}
                onPlatformDriftChange={setPlatformDrift}
              />
              {drift.length === 0 ? (
                <OpsEmptyState title="انحراف بازی نیست" />
              ) : filteredDrift.length === 0 ? (
                <OpsEmptyState title="موردی در این فیلتر نیست" />
              ) : (
                <FleetDriftInventoryList
                  rows={filteredDrift}
                  selectedKey={selectedDriftKey}
                  onSelect={setSelectedDriftKey}
                  renderActions={renderDriftActions}
                  showOrg={platformDrift}
                />
              )}
            </Card>

            {selectedDriftRow && (
              <InspectPanel title="جزئیات انحراف" className="content-inspect-panel">
                <FleetDriftInspectSummary
                  item={selectedDriftRow}
                  actions={renderDriftActions(selectedDriftRow)}
                />
              </InspectPanel>
            )}
          </div>
        </div>
      )}

      {passwordAction && (
        <PasswordConfirmModal
          title={
            passwordAction.kind === "promote_production"
              ? "ارتقای حلقه به تولید"
              : passwordAction.kind === "verify_mismatch"
                ? "ثبت عدم تطبیق"
                : "معافیت تمرین بازگردانی"
          }
          description={
            passwordAction.kind === "promote_production"
              ? "این کار استقرار را شروع نمی‌کند؛ فقط برچسب حلقه در CMS عوض می‌شود."
              : passwordAction.kind === "verify_mismatch"
                ? "عدم تطبیق به‌عنوان انحراف نمایشی ثبت می‌شود؛ اقدام روتر انجام نمی‌شود."
                : "معافیت تمرین بازگردانی نیاز به تأیید رمز دارد."
          }
          password={password}
          onPasswordChange={setPassword}
          confirming={confirming}
          confirmLabel="تأیید با رمز"
          tone={passwordAction.kind === "verify_mismatch" ? "danger" : "primary"}
          onCancel={() => {
            setPasswordAction(null);
            setPassword("");
          }}
          onConfirm={onPasswordConfirm}
        />
      )}
    </div>
  );
}
